import React, { useEffect, useState, useRef, useCallback } from 'react';
import './AboutMe.css';

const AboutMe = ({ isDarkMode }) => {
  const [imageSrc, setImageSrc] = useState('');
  const [currentText, setCurrentText] = useState('');
  const [cursor, setCursor] = useState('_');
  const [currentColor, setCurrentColor] = useState('');
  const [scanComplete, setScanComplete] = useState(false);
  const heroRef = useRef(null);
  const memojiCardRef = useRef(null);

  // Scan-in reveal
  useEffect(() => {
    const timer = setTimeout(() => setScanComplete(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  // Memoji source
  useEffect(() => {
    setImageSrc('/me1.png');
  }, [isDarkMode]);

  // Set initial color based on theme
  useEffect(() => {
    setCurrentColor(isDarkMode ? '#00ff87' : '#047857');
  }, [isDarkMode]);

  // Typing effect
  useEffect(() => {
    const colors = isDarkMode
      ? ['#00ff87', '#00e5ff', '#60a5fa', '#34d399', '#22d3ee']
      : ['#047857', '#0e7490', '#3b82f6', '#10b981', '#06b6d4'];
    const words = ['Script. Solve. Sweat. Repeat.'];
    let currentWordIndex = 0;
    let letterIndex = 0;
    let isDeleting = false;

    const intervalId = setInterval(() => {
      const currentWord = words[currentWordIndex];
      setCursor('_');
      setCurrentText(currentWord.substring(0, letterIndex));

      if (!isDeleting && letterIndex === currentWord.length) {
        isDeleting = true;
      } else if (isDeleting && letterIndex === 0) {
        isDeleting = false;
        currentWordIndex = (currentWordIndex + 1) % words.length;
        setCurrentColor(colors[Math.floor(Math.random() * colors.length)]);
      }
      letterIndex = isDeleting ? letterIndex - 1 : letterIndex + 1;
    }, 220);

    return () => clearInterval(intervalId);
  }, [isDarkMode]);

  // 3D tilt on Memoji card
  const handleMouseMove = useCallback((e) => {
    if (!memojiCardRef.current) return;
    const card = memojiCardRef.current;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -12;
    const rotateY = ((x - centerX) / centerX) * 12;
    card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.05)`;
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!memojiCardRef.current) return;
    memojiCardRef.current.style.transform = 'perspective(800px) rotateX(0) rotateY(0) scale(1)';
  }, []);

  return (
    <>
      <div className='spacer21' />

      <div
        ref={heroRef}
        className={`aboutMe ${isDarkMode ? 'dark-mode' : 'light-mode'} ${scanComplete ? 'scan-done' : 'scanning'}`}
      >
        {/* Scan line overlay */}
        <div className={`scan-line-overlay ${scanComplete ? 'done' : ''}`}>
          <div className="scan-line" />
        </div>

        <div className='center-div'>
          {/* Memoji in glass container with 3D tilt */}
          <div className="leftImageAbout">
            <div
              ref={memojiCardRef}
              className="memoji-glass"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <img className="myImage" src={imageSrc} alt="My Memoji" />
              <div className="memoji-glow" />
            </div>
          </div>

          {/* Typing tagline — shown on mobile below image */}
          <div className="quote">
            <div className="tt" style={{ color: currentColor }}>
              {currentText}
              <span className={cursor === '_' ? 'blinking-cursor' : ''}>{cursor}</span>
            </div>
          </div>

          <div className="rightTextAbout">
            <div className="aboutmeHead">
              I'm Vedant. <span>Bridging Code</span>, <span>Data</span>, and <span>Finance</span>.
            </div>
            <div className="aboutmeBody">
              I'm a builder at heart — someone who finds joy in simplifying complexity.
              Whether I'm deploying secure microservices at <strong>Wells Fargo</strong> or analyzing financial models for grad school,
              I aim to craft experiences that are elegant, robust, and practical.
            </div>
            <div className="aboutmeBorder" />
            <div className="aboutmeBody">
              I care deeply about merging <strong>finance and tech</strong>.
              After an internship at <strong>American Express</strong>, I found myself drawn to the fast-paced world of quant and trading systems.
              My current path blends this interest with software engineering and applied mathematics.
            </div>
            <div className="aboutmeBody">
              I value <strong>consistency over chaos</strong>, substance over noise. I don't chase shiny trends — I prefer thoughtful execution, long-term learning, and quiet, intentional work that moves the needle.
            </div>
            <div className="aboutmeBody">
              Outside of work, I'm doing 50 pushups a day, biking to class, or lifting weights.<br />
              I believe that stillness, reflection, and discipline are as crucial as hustle. No wonder the people call me the <span className="amazonspan">Awesomeman</span>.
            </div>

            <div className="social-icons">
              <a href="https://github.com/VedantSupnekar" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-github"></i>
              </a>
              <a href="https://linkedin.com/in/vedantsupnekar" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-linkedin-in"></i>
              </a>
              <a href="mailto:vedant.supnekar@gmail.com" target="_blank" rel="noopener noreferrer">
                <i className="fa-solid fa-at"></i>
              </a>
            </div>

            <div className="resume-about">
              <a
                className="resume-button-1"
                target="_blank"
                rel="noopener noreferrer"
                href={`${process.env.PUBLIC_URL}/resume.pdf`}
              >
                <i className="fa-solid fa-download" style={{ marginRight: '8px' }}></i>
                Download Resume
                <span className="btn-glow" />
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="spacer show-no"></div>
    </>
  );
};

export default AboutMe;
